import { type ReactElement, useContext, useMemo } from "react"

import { useTranslation } from "react-i18next"

import { Typography } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import { Context } from "../../../features/ModelConfigurations/Context"
import { formatTableData } from "../../../features/ModelConfigurations/Helpers"
import type { ConfigFormikProps } from "../../../features/ModelConfigurations/Interfaces"
import { getEnvVar, isEnvVariableTruthy } from "../../../utils/genericHelpers"
import { ChartsContainer } from "../../graphs/ChartsContainer"
import { EVALUATION_METRIC_KEYS, getLocalizedMetricNames } from "./Constants"

import styles from "./EvaluationMetrics.module.scss"

/**
 * A component that displays the evaluation metrics for the current threshold value.
 * It uses the `useformikcontext` hook to get the labels and the currentThresholdMetricData.
 * It displays the evaluation metrics in a table with 4 columns: Evaluation, Score, Positive Label and Negative Label.
 * The table rows are generated from the TABLE_ROWS array in the Constants file.
 * The table cells are formatted using the formatTableData function in the Helpers file.
 */
export function EvaluationMetrics(): ReactElement {
  const { currentThresholdMetricData } = useContext(Context)
  const { t } = useTranslation("ModelConfigurations", { keyPrefix: "metrics.evaluation" })

  const {
    values: { labels, negativeLabel, positiveLabel },
  } = useFormikContext<ConfigFormikProps>()

  const isCoverageEnabled = isEnvVariableTruthy(String(getEnvVar("AUTOML_COVERAGE_ENABLED")))

  const localizedRows = getLocalizedMetricNames(t)

  const adjustedRows = useMemo((): string[] => {
    let tempRows = localizedRows

    if (!isCoverageEnabled) tempRows = tempRows.filter((row) => row !== t("coverage"))

    return tempRows
  }, [isCoverageEnabled, localizedRows, t])

  const originalKeys = useMemo((): string[] => {
    let keys = Object.values(EVALUATION_METRIC_KEYS)
    if (!isCoverageEnabled) keys = keys.filter((key) => key !== "coverage")
    return keys
  }, [isCoverageEnabled])

  return (
    <section>
      <ChartsContainer
        isLoading={false}
        title={t("evaluationMetrics")}
        gutterBottom={true}
        graphHeight={isCoverageEnabled ? 289 : 260}
      >
        <div className={styles.tableContainer}>
          <table>
            <thead>
              <tr>
                <td>
                  <Typography variant="a">{t("evaluation")}</Typography>
                </td>
                <td>
                  <Typography variant="a">{t("score")}</Typography>
                </td>
                <td>
                  <Typography variant="a">{labels[1]}</Typography>
                </td>
                <td>
                  <Typography variant="a">{labels[0]}</Typography>
                </td>
              </tr>
            </thead>
            <tbody>
              {adjustedRows.map((localizedRow, index) => {
                // Get the original key for data lookup
                const originalKey = originalKeys[index]

                if (!originalKey) return null

                return (
                  <tr key={originalKey}>
                    <td>
                      <Typography variant="a" variantColor={2} textTransform="capitalize">
                        {localizedRow.split("_").join(" ")}
                      </Typography>
                    </td>
                    <td title={(currentThresholdMetricData?.[originalKey] ?? t("notAvailable")).toString()}>
                      <Typography variant="p" variantColor={2}>
                        {formatTableData(currentThresholdMetricData, originalKey)}
                      </Typography>
                    </td>
                    <td
                      title={(
                        currentThresholdMetricData?.[`${originalKey}_${negativeLabel?.name || ""}`] ?? t("notAvailable")
                      ).toString()}
                    >
                      <Typography variant="p" variantColor={2}>
                        {formatTableData(currentThresholdMetricData, `${originalKey}_${negativeLabel?.name || ""}`)}
                      </Typography>
                    </td>
                    <td
                      title={(
                        currentThresholdMetricData?.[`${originalKey}_${positiveLabel?.name || ""}`] ?? t("notAvailable")
                      ).toString()}
                    >
                      <Typography variant="p" variantColor={2}>
                        {formatTableData(currentThresholdMetricData, `${originalKey}_${positiveLabel?.name || ""}`)}
                      </Typography>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </ChartsContainer>
    </section>
  )
}
